import React, { useState } from 'react';
import { IDictionary } from '../../../../../util/UtilityTypes';
import { CoreTeamsFix } from '../../../../../models/TeamsFixTypes';
import BoolInput from '../../../../misc/BoolInput';
import NullableIntInput from '../../../../misc/NullableIntInput';
import { Button, Card, CardBody, CardHeader, Col, Collapse, Row, Alert } from 'reactstrap';
import TeamsFixStandardSettings from './TeamsFixStandardSettings';
import TeamsFixArchivedSettings from './TeamsFixArchivedSettings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

interface TeamsFixProps {
    teamsFix: CoreTeamsFix
    disabled: boolean
    onChange: (x: CoreTeamsFix) => void
    teamsFixEnabledSaved: boolean
    validation: string[]
}

const initialIsOpen: IDictionary<string, boolean> = {
    teamsFix: true,
    active: true,
    archived: true,
}

const TeamsFixComponent: React.FC<TeamsFixProps> = (props) => {
    const { t } = useTranslation(['configuration']);

    const [isOpen, setOpen] = useState(initialIsOpen);

    const updateConfig = (change: Partial<CoreTeamsFix>) => {
        props.onChange({ ...props.teamsFix, ...change });
    }

    return (<Card>
        <CardHeader tag="h5">
            <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen({ ...isOpen, teamsFix: !isOpen.teamsFix })}>{isOpen.teamsFix ? '-' : '+'}</Button>

            {t('oneRosterConfiguration.teamsFix.title', { ns: 'configuration' })}
        </CardHeader>
        <Collapse isOpen={isOpen["teamsFix"]}>
            <CardBody>
                {!props.teamsFixEnabledSaved && props.teamsFix.enabled &&
                    <Alert style={{ display: 'inline-block' }} color="info"><FontAwesomeIcon icon="exclamation-circle" className="me-2" /> {t('oneRosterConfiguration.teamsFix.enableWarning', { ns: 'configuration' })}</Alert>
                }
                <BoolInput
                    title={t('oneRosterConfiguration.teamsFix.enable', { ns: 'configuration' })}
                    name="enabled"
                    value={props.teamsFix.enabled}
                    disabled={props.disabled}
                    updateValue={(name, value) => updateConfig({ [name]: value })}
                />

                {props.teamsFix.enabled && <>
                    <h5>{t('oneRosterConfiguration.teamsFix.timeZone.title', { ns: 'configuration' })}</h5>
                    <NullableIntInput
                        title={<>{t('oneRosterConfiguration.teamsFix.timeZone.timeZoneId', { ns: 'configuration' })} <a href="https://learn.microsoft.com/en-us/previous-versions/office/sharepoint-csom/jj171282(v=office.15)" target="_blank" rel="noreferrer">https://learn.microsoft.com/en-us/previous-versions/office/sharepoint-csom/jj171282(v=office.15)</a></>}
                        name="timeZoneId"
                        value={props.teamsFix.timeZoneId}
                        updateValue={(name, value) => updateConfig({ [name]: value })}
                        disabled={props.disabled}
                        defaultValue={4}
                        invalidFeedback={t('oneRosterConfiguration.teamsFix.timeZone.invalidTimeZone', { ns: 'configuration' })}
                        invalid={props.validation.indexOf('timeZoneId') !== -1}
                    />

                    <Row>
                        <Col xl={6}>
                            <Card className="mb-3">
                                <CardHeader tag="h5">
                                    <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen({ ...isOpen, active: !isOpen.active })}>{isOpen.active ? '-' : '+'}</Button>
                                    {t('oneRosterConfiguration.teamsFix.settings.activeTitle', { ns: 'configuration' })}
                                </CardHeader>
                                <Collapse isOpen={isOpen["active"]}>
                                    <CardBody>
                                        <TeamsFixStandardSettings
                                            name='standard'
                                            disabled={props.disabled}
                                            teamsFixSettings={props.teamsFix.standardSettings}
                                            onChange={(x) => updateConfig({ standardSettings: x })}
                                        />
                                    </CardBody>
                                </Collapse>
                            </Card>
                        </Col>
                        <Col xl={6}>
                            <Card>
                                <CardHeader tag="h5">
                                    <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen({ ...isOpen, archived: !isOpen.archived })}>{isOpen.archived ? '-' : '+'}</Button>
                                    {t('oneRosterConfiguration.teamsFix.settings.archivedTitle', { ns: 'configuration' })}
                                </CardHeader>
                                <Collapse isOpen={isOpen["archived"]}>
                                    <CardBody>
                                        <TeamsFixArchivedSettings
                                            name='archived'
                                            disabled={props.disabled}
                                            teamsFixSettings={props.teamsFix.archivedSettings}
                                            onChange={(x) => updateConfig({ archivedSettings: x })}
                                        />
                                    </CardBody>
                                </Collapse>
                            </Card>
                        </Col>
                    </Row>
                </>}
            </CardBody>
        </Collapse>
    </Card >);
}

export default TeamsFixComponent;
